import { yupResolver } from '@hookform/resolvers/yup'
import { find, map } from 'lodash-es'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import SelectField from '@/components/select-field'
import { Button, Checkbox, Modal } from '@/components/ui'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import FormGroup from '@/core/components/FormGroup'
import { cn } from '@/lib/utils'
import { addDays, setHours, setMinutes } from 'date-fns'
import { AlertCircle } from 'lucide-react'
import {
  Artist,
  ArtistPerformance,
  ArtistPerformanceResponse,
  Days,
  Stage,
  Stages,
} from '../../common/models'
import {
  dateToTimeOptions,
  formatDate,
  timpestampToDate,
} from '../../core/constants/times'
import {
  createPerformance,
  deletePerformance,
  getAllArtists,
  getAllPerformances,
  updatePerformance,
} from '../../core/services/artist'
import { getAllStages } from '../../core/services/stage'
import { MessageBox } from './messages/MessageBox'

export interface IPerfArtistValues {
  artist: string
  stage: string
  date?: string | null
  startTime?: string | null
  endTime?: string | null
  headliner: boolean
}

export interface CreateStageModalProps {
  editMode: boolean
  showCreateModal: boolean
  artists: Artist[]
  performance: ArtistPerformanceResponse
  stage: Stage
  page: number
  setEditMode: (edit: boolean) => void
  setArtists: (data: ArtistPerformance[]) => void
  setShowCreateModal: (show: boolean) => void
  setLoading: (show: boolean) => void
  days: Days[] | undefined
}

export const CreatePerfArtistModal = (
  props: CreateStageModalProps,
): JSX.Element => {
  const {
    editMode,
    setEditMode,
    showCreateModal,
    setShowCreateModal,
    performance,
    stage,
    artists,
    page,
    setArtists,
    days,
    setLoading,
  } = props
  const [stages, setStages] = useState<Stages[]>([])

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const [artistResults, setArtistResults] = useState<Artist[]>(artists)
  const [artistSearchLoading, setArtistSearchLoading] = useState<boolean>(false)

  useEffect(() => {
    setArtistResults(artists)
  }, [artists])

  const handleArtistSearch = async (query: string) => {
    setArtistSearchLoading(true)
    try {
      const { data } = await getAllArtists({ name: query || null, size: 50 })
      setArtistResults(data.content)
    } catch (e) {
      console.error(e)
    } finally {
      setArtistSearchLoading(false)
    }
  }

  useEffect(() => {
    // prettier-ignore
    (async () => {
      setLoading(true)
      try {
        const response = await getAllStages()
        setStages(response.data.content)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()

    return () => {
      reset()
    }
  }, [])

  const params = useParams()

  useEffect(() => {
    const foundStage = find(stages, (stg) => stg.id === stage?.id)
    if (editMode) {
      if (foundStage) {
        setValue('stage', foundStage.id, { shouldValidate: true })
      } else {
        setValue('stage', 'Select Stage')
      }

      if (performance) {
        setValue('date', performance.festivalDayId || '', {
          shouldValidate: false,
        })
        if (performance?.startTime) {
          setValue(
            'startTime',
            dateToTimeOptions(timpestampToDate(performance?.startTime)),
            { shouldValidate: false },
          )
        }
        if (performance?.endTime) {
          setValue(
            'endTime',
            dateToTimeOptions(timpestampToDate(performance?.endTime)),
            { shouldValidate: false },
          )
        }
      } else {
        setValue('startTime', '')
        setValue('endTime', '')
        setValue('date', '')
      }
      setValue('headliner', performance?.headliner || false)

      if (performance?.artist?.id) {
        setValue('artist', performance.artist.id, { shouldValidate: true })
      } else {
        setValue('artist', '')
      }
    } else {
      setValue('date', '')
      setValue('artist', '')
      setValue('startTime', '')
      setValue('endTime', '')
      setValue('headliner', false)
    }
  }, [performance, editMode, stage])

  const schema = yup.object().shape({
    artist: yup.string().required('Field is required'),
    stage: yup.string().required('Field is required'),
    date: yup.string().nullable().optional(),
    startTime: yup.string().nullable().optional(),
    endTime: yup.string().nullable().optional(),
    headliner: yup.boolean().default(false).required('Field is required'),
  })

  const {
    handleSubmit,
    control,
    formState: { errors, dirtyFields, isValid, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<IPerfArtistValues>({
    mode: 'onChange',
    resolver: yupResolver<IPerfArtistValues>(schema),
    defaultValues: {
      artist: '',
      stage: params.stageId,
      date: '',
      startTime: '',
      endTime: '',
      headliner: false,
    },
  })

  const artistWatch = watch('artist')

  // Keep the currently selected artist in the list so its label stays visible
  // even when the backend search results no longer include it.
  const artistOptions = (() => {
    const selected =
      find(artists, (art) => art.id === artistWatch) ||
      (performance?.artist?.id === artistWatch ? performance.artist : undefined)
    const options = map(artistResults, (art) => ({
      value: art.id,
      label: art.name,
    }))
    if (selected && !options.some((opt) => opt.value === selected.id)) {
      return [{ value: selected.id, label: selected.name }, ...options]
    }
    return options
  })()

  const handleClose = () => {
    setShowCreateModal(false)
    setEditMode(false)
    reset()
  }

  const submit = async (values: IPerfArtistValues) => {
    setLoading(true)

    try {
      const selectedDay = days?.find((d) => d.id === values.date)
      const dayStart = new Date(selectedDay?.startAt || '')
      let finalStartTime,
        finalEndTime = null

      // A festival day can span past midnight (e.g. 16:00 -> 06:00 next day).
      // A picked time-of-day earlier than the day's start belongs to the
      // following calendar day, so roll it forward when that's the case.
      const resolveTime = (time: string) => {
        let result = setHours(dayStart, +time.split(':')[0])
        result = setMinutes(result, +time.split(':')[1])
        if (result < dayStart) {
          result = addDays(result, 1)
        }
        return result
      }

      if (values.startTime) {
        finalStartTime = resolveTime(values.startTime)
      }
      if (values.endTime) {
        finalEndTime = resolveTime(values.endTime)
      }

      if (editMode) {
        const { status } = await updatePerformance(performance.id, {
          artistId: values.artist,
          stageId: values.stage,
          startTime: finalStartTime ? finalStartTime.getTime() : null,
          endTime: finalEndTime ? finalEndTime.getTime() : null,
          festivalDayId: values.date,
          headliner: values.headliner,
        })
        const artistsResponse = await getAllPerformances({
          page: page - 1,
          size: 30,
          stageId: params.stageId,
          sortBy: 'startTime',
        })
        setArtists(artistsResponse.data.content)
        if (status === 200 && artistsResponse.status === 200) {
          handleClose()
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } else {
        const { status } = await createPerformance({
          artistId: values.artist,
          stageId: values.stage,
          startTime: finalStartTime ? finalStartTime.getTime() : null,
          endTime: finalEndTime ? finalEndTime.getTime() : null,
          festivalDayId: values.date,
          headliner: values.headliner,
        })
        const artistsResponse = await getAllPerformances({
          page: page - 1,
          size: 30,
          stageId: params.stageId,
          sortBy: 'startTime',
        })
        setArtists(artistsResponse.data.content)
        if (status === 200 && artistsResponse.status === 200) {
          handleClose()
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = () => {
    // prettier-ignore
    (async () => {
      setLoading(true)
      try {
        const { status } = await deletePerformance([performance.id])
        const artistsResponse = await getAllPerformances({
          page: page - 1,
          size: 30,
          stageId: params.stageId,
          sortBy: 'startTime',
        })
        setArtists(artistsResponse.data.content)
        if (status === 204 && artistsResponse.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
    handleClose()
  }

  const generateClassColor = (artistId: string) => {
    const art = find(artists, (a) => a.id === artistId)
    if (art && art.hasNonWrapperStage) {
      return 'text-accent-1'
    }
  }

  return (
    <>
      <Modal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Stages"
              subtitle={
                editMode
                  ? `Edit Artist on stage: ${stage?.translations?.[0]?.name}`
                  : 'Add New Artist'
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {editMode ? (
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleDelete}
                  variant="destructive"
                  className="mr-auto"
                >
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
                <Button
                  form="artist-perf-form"
                  type="reset"
                  variant="secondary"
                  className="bg-accent-4 text-monochrome-5 hover:bg-accent-4/90"
                >
                  Clear
                  <Icon
                    icon="refresh"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </div>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                form="artist-perf-form"
                type="submit"
                variant="primary"
                loading={isSubmitting}
                disabled={
                  isSubmitting ||
                  (Object.keys(dirtyFields).length === 0 && isValid)
                }
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="artist-perf-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Artist" error={errors?.artist?.message}>
            <Controller
              name="artist"
              control={control}
              render={({ field }) => (
                <SelectField
                  outline
                  searchable
                  searchPlaceholder="Search artists..."
                  placeholder="Select Artist"
                  options={artistOptions}
                  onSearch={handleArtistSearch}
                  searchLoading={artistSearchLoading}
                  value={field.value}
                  onValueChange={field.onChange}
                  error={errors?.artist?.message}
                  triggerClassName={cn(
                    generateClassColor(artistWatch) === 'text-accent-1' &&
                      'ring-2 ring-green-500',
                    'h-[44px] rounded-2xl',
                  )}
                />
              )}
            />
          </FormGroup>

          <FormGroup label="Stage" error={errors?.stage?.message}>
            <Controller
              name="stage"
              control={control}
              render={({ field }) => (
                <SelectField
                  outline
                  placeholder="Select Stage"
                  options={map(stages, (stg) => ({
                    value: stg.id,
                    label: stg.name,
                  }))}
                  value={field.value}
                  onValueChange={field.onChange}
                  error={errors?.stage?.message}
                  triggerClassName="h-[44px] rounded-2xl"
                />
              )}
            />
          </FormGroup>

          <FormGroup label="Date (Optional)" error={errors?.date?.message}>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <SelectField
                  outline
                  placeholder="Select Date"
                  options={map(days, (day) => {
                    const name = find(
                      day.translations,
                      (t) => t.locale === 'en',
                    )?.name
                    return {
                      value: day.id,
                      label: `${formatDate(day.startAt)} ( ${name} )`,
                    }
                  })}
                  value={field.value || ''}
                  onValueChange={field.onChange}
                  error={errors?.date?.message}
                  triggerClassName="h-[44px] rounded-2xl"
                />
              )}
            />
          </FormGroup>

          <div className="flex gap-4">
            <div className="flex-1">
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => {
                  const timeValue =
                    field.value && field.value !== ''
                      ? (() => {
                          const [hours, minutes] = field.value.split(':')
                          const date = new Date()
                          date.setHours(
                            parseInt(hours),
                            parseInt(minutes),
                            0,
                            0,
                          )
                          return date
                        })()
                      : null
                  return (
                    <DateTimePicker
                      mode="time"
                      label="Start Time (Optional)"
                      align="start"
                      placeholder="Select start time"
                      value={timeValue}
                      onChange={(date) => {
                        if (date) {
                          const hours = String(date.getHours()).padStart(2, '0')
                          const minutes = String(date.getMinutes()).padStart(
                            2,
                            '0',
                          )
                          field.onChange(`${hours}:${minutes}`)
                        } else {
                          field.onChange('')
                        }
                      }}
                      hasError={!!errors?.startTime?.message}
                      minuteStep={5}
                      className="h-[44px] rounded-2xl"
                    />
                  )
                }}
              />
            </div>

            <div className="flex-1">
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => {
                  const timeValue =
                    field.value && field.value !== ''
                      ? (() => {
                          const [hours, minutes] = field.value.split(':')
                          const date = new Date()
                          date.setHours(
                            parseInt(hours),
                            parseInt(minutes),
                            0,
                            0,
                          )
                          return date
                        })()
                      : null
                  return (
                    <DateTimePicker
                      mode="time"
                      align="start"
                      label="End Time (Optional)"
                      placeholder="Select end time"
                      value={timeValue}
                      onChange={(date) => {
                        if (date) {
                          const hours = String(date.getHours()).padStart(2, '0')
                          const minutes = String(date.getMinutes()).padStart(
                            2,
                            '0',
                          )
                          field.onChange(`${hours}:${minutes}`)
                        } else {
                          field.onChange('')
                        }
                      }}
                      hasError={!!errors?.endTime?.message}
                      minuteStep={5}
                      className="h-[44px] rounded-2xl"
                    />
                  )
                }}
              />
            </div>
          </div>

          {editMode && performance.alerts && (
            <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto">
              {map(performance.alerts, (alert) => (
                <Alert key={alert} className="bg-yellow-100 border-yellow-200">
                  <AlertCircle className="h-4 w-4 text-yellow-800" />
                  <AlertDescription className="text-yellow-800">
                    {alert}
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          )}

          <FormGroup
            label="Headliner"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'headliner',
            }}
          >
            <Controller
              name="headliner"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="headliner"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
